import { Link } from "react-router-dom";
import {
  Target, Eye, Heart, Scale, BadgeCheck, HeartHandshake, ShieldCheck, Home as HomeIcon,
  Lightbulb, ArrowRight, MessageCircle, MapPin, CalendarDays,
} from "lucide-react";

const HISTORY_IMG = "https://images.unsplash.com/photo-1597764690523-15bea4c581c9?w=1000&q=80";

const VALUES = [
  { icon: Heart, text: "Respeito e cuidado com as pessoas" },
  { icon: Scale, text: "Ética e transparência em todas as relações" },
  { icon: BadgeCheck, text: "Compromisso com a qualidade dos produtos e serviços" },
  { icon: HeartHandshake, text: "Atendimento humanizado e personalizado" },
  { icon: ShieldCheck, text: "Responsabilidade, confiança e credibilidade" },
  { icon: HomeIcon, text: "Valorização da tradição familiar" },
  { icon: Lightbulb, text: "Busca constante por inovação e aperfeiçoamento" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="text-white" style={{ background: "linear-gradient(120deg, #0B2038 0%, #0F2E52 55%, #1565C0 100%)" }}>
        <div className="max-w-4xl mx-auto px-6 py-16 lg:py-24 text-center fade-up">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-brand-300 mb-4">Cuidar · Reabilitar · Transformar</p>
          <h1 className="font-heading text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            Mais de quatro décadas dedicadas à <span className="text-brand-300">mobilidade e à qualidade de vida</span>
          </h1>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-sm text-slate-300">
            <span className="flex items-center gap-1.5"><CalendarDays size={15} className="text-brand-300" /> Fundada em 1981</span>
            <span className="flex items-center gap-1.5"><MapPin size={15} className="text-brand-300" /> Dourados — MS</span>
            <span className="flex items-center gap-1.5"><Heart size={15} className="text-brand-300" /> Empresa familiar</span>
          </div>
        </div>
      </section>

      {/* HISTÓRIA */}
      <section className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-brand-600 mb-3">Nossa história</p>
          <h2 className="font-heading text-3xl font-semibold text-slate-900 mb-5">Uma tradição de cuidado que começou em 1981</h2>
          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p>
              A Ortopédica São Paulo é uma empresa familiar que há mais de quatro décadas dedica-se ao cuidado,
              à mobilidade e à qualidade de vida de seus clientes. Fundada em 1981, em Dourados, pelo
              Sr. Clóvis Franco e sua esposa, Maria Antônia Franco, a empresa nasceu com o propósito de oferecer
              produtos ortopédicos de qualidade, atendimento humanizado e soluções que promovam bem-estar e independência.
            </p>
            <p>
              Ao longo dos anos, a dedicação da família Franco, aliada ao compromisso com a ética, o respeito e a
              confiança, consolidou a Ortopédica São Paulo como uma referência no segmento ortopédico na região.
              Hoje, a empresa mantém viva a tradição iniciada por seus fundadores, unindo experiência, inovação e
              atendimento personalizado para atender às necessidades de cada cliente.
            </p>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[["1981", "Ano de fundação"], ["+40", "Anos de tradição"], ["2", "Gerações da família Franco"]].map(([n, l]) => (
              <div key={l} className="border border-slate-200 rounded-xl bg-white p-4 text-center">
                <p className="font-heading text-2xl font-semibold text-brand-600">{n}</p>
                <p className="text-xs text-slate-500 mt-1 leading-tight">{l}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="relative">
            <img src={HISTORY_IMG} alt="Atendimento humanizado" className="rounded-2xl object-cover w-full h-[420px] shadow-xl" />
            <div className="absolute -bottom-5 -left-5 bg-white border border-slate-200 rounded-xl shadow-lg px-5 py-4 hidden sm:block">
              <p className="text-xs text-slate-400">Desde</p>
              <p className="font-heading text-3xl font-semibold text-[#0B2038]">1981</p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSÃO E VISÃO */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid="about-mission">
            <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center mb-4"><Target size={24} /></div>
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">Missão</h3>
            <p className="text-slate-600 leading-relaxed">
              Promover qualidade de vida, mobilidade e bem-estar por meio de produtos ortopédicos de excelência,
              oferecendo atendimento humanizado, orientação especializada e soluções que contribuam para a saúde
              e a autonomia de nossos clientes.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid="about-vision">
            <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center mb-4"><Eye size={24} /></div>
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">Visão</h3>
            <p className="text-slate-600 leading-relaxed">
              Ser reconhecida como a principal referência em produtos ortopédicos no sul de Mato Grosso do Sul,
              destacando-se pela confiança, tradição, inovação e compromisso com a satisfação de nossos clientes.
            </p>
          </div>
        </div>
      </section>

      {/* VALORES */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-brand-600 mb-3">O que nos guia</p>
          <h2 className="font-heading text-3xl font-semibold text-slate-900">Nossos valores</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="about-values">
          {VALUES.map((v) => (
            <div key={v.text} className="flex items-center gap-4 border border-slate-200 rounded-xl bg-white p-5 hover:border-brand-400 hover:shadow-sm transition-all">
              <div className="w-10 h-10 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0"><v.icon size={20} /></div>
              <p className="text-sm font-medium text-slate-700 leading-snug">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="rounded-2xl text-white px-8 py-12 lg:px-14 text-center" style={{ background: "linear-gradient(120deg, #0B2038 0%, #1565C0 100%)" }}>
          <h2 className="font-heading text-2xl lg:text-3xl font-semibold">Venha fazer parte dessa história</h2>
          <p className="text-white/80 mt-2 max-w-xl mx-auto">Conheça nossos produtos ou fale com quem entende de mobilidade há mais de 40 anos.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-4">
            <Link to="/produtos" data-testid="about-products-btn" className="bg-white text-brand-700 hover:bg-brand-50 font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2 transition-colors">
              Ver produtos <ArrowRight size={17} />
            </Link>
            <a href="https://wa.me/5567998098308?text=Olá! Vim pela página Sobre Nós do site." target="_blank" rel="noreferrer"
              data-testid="about-whatsapp-btn" className="border border-white/40 hover:border-white text-white font-medium px-6 py-3 rounded-full inline-flex items-center gap-2 transition-colors">
              <MessageCircle size={17} className="text-emerald-400" /> Falar no WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
